import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@material-ui/core";
import { v4 as uuid } from "uuid";
import useStyles from "./MultiSelectionTableStyle";
import LaboratoryNotificationListsApi from "../../../../../../api/services/LaboratoryNotificationListsApi";
import useEnv from "../../../../../../hooks/env";
import CustomProgress from "../../../../../../components/Alert/CustomProgress";
import { ErrorSnackbarContext } from "../../../../../../contexts/ErrorSnackbarContext";
import CustomButton from "../../../../../../components/Button/CustomButton";
import { Field, TableRowView } from "../../../NotificationsDialog/interfaces/TableRowView";
import UserAction from "../../../../constants/UserAction";
import StyledTooltip from "../../../../../../components/Wrapper/StyledTooltip";
import TestCasesOverviewTableFilterLabel from "../../../../../../components/TableColumnFilter/TableColumnFilter";
import useFindCommonIds from "./useFindCommonIds";
import TableColumnUserFilter from "./components/TableColumnFilter/TableColumnUserFilter";
import { NotificationListPagedResponse } from "../../../../../../api/models/test-request/LaboratoryNotificationLists";

enum TableColumnId {
  checkbox = "checkbox",
  recipients = "recipients",
  businessUnit = "businessUnit",
  marketSegment = "marketSegment",
  applicationField = "applicationField",
}

const columns = [
  { id: TableColumnId.checkbox, label: "" },
  { id: TableColumnId.recipients, label: "Users" },
  { id: TableColumnId.businessUnit, label: "Business Unit" },
  { id: TableColumnId.marketSegment, label: "Market Segment" },
  { id: TableColumnId.applicationField, label: "Application" },
];

interface MultiSelectionTableProps {
  laboratoryId: number;
  action: UserAction;
  setAction: React.Dispatch<React.SetStateAction<UserAction>>;
  selectedRows: TableRowView[];
  setSelectedRows: React.Dispatch<React.SetStateAction<TableRowView[]>>;
  isNotificationDialogOpen: boolean;
  setIsNotificationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ActiveUserFilter {
  recipients: Field[];
}

interface ActiveFilter {
  businessUnit: string[];
  marketSegment: string[];
  applicationField: string[];
}

const MultiSelectionTable = (props: MultiSelectionTableProps) => {
  const {
    laboratoryId,
    setAction,
    selectedRows,
    setSelectedRows,
    isNotificationDialogOpen,
    setIsNotificationDialogOpen,
  } = props;
  const classes = useStyles();

  const { api } = useEnv();
  const notificationListsApi = new LaboratoryNotificationListsApi(api);

  const { handleHttpError } = useContext(ErrorSnackbarContext);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [filterOptions, setFilterOptions] = useState<any>();
  const [activeUserFilters, setActiveUserFilters] = useState<ActiveUserFilter>({
    recipients: [],
  });
  const [activeFilters, setActiveFilters] = useState<ActiveFilter>({
    businessUnit: [],
    marketSegment: [],
    applicationField: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<TableRowView[]>([]);

  const handleChangePage = (event: React.MouseEvent<unknown> | null, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const columnUserFilterChange = (data: Field[]) => {
    setActiveUserFilters({ recipients: data });
  };

  const columnFilterChange = (data: string[], column: string) => {
    const filterToUpdate = { [column]: data };
    setActiveFilters({ ...activeFilters, ...filterToUpdate });
  };

  const getFilterOptions = (column: string) => {
    const filterOptionsMap = {
      businessUnit: filterOptions?.businessUnitFilterOptions,
      marketSegment: filterOptions?.marketSegmentFilterOptions,
      applicationField: filterOptions?.applicationFieldFilterOptions,
    } as { [key: string]: Field[] };

    return (filterOptionsMap[column] || []).map((option) => option.name);
  };

  const getActiveFilter = (column: string) => {
    let activeFilter: string[] = [];
    switch (column) {
      case TableColumnId.businessUnit:
        activeFilter = activeFilters.businessUnit;
        break;
      case TableColumnId.marketSegment:
        activeFilter = activeFilters.marketSegment;
        break;
      case TableColumnId.applicationField:
        activeFilter = activeFilters.applicationField;
        break;
      default:
        return activeFilter;
    }
    return activeFilter;
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      rows.forEach((row) => {
        row.checkbox = true;
      });
      setSelectedRows(rows);
    } else {
      rows.forEach((row) => {
        row.checkbox = false;
      });
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (event: React.MouseEvent<unknown>, currentRow: TableRowView) => {
    let newSelected: TableRowView[] = [];

    if (!currentRow.checkbox) {
      newSelected = newSelected.concat(selectedRows, currentRow);
      setSelectedRows(newSelected);
    } else {
      const stillSelected = selectedRows.filter((row) => JSON.stringify(row) !== JSON.stringify(currentRow));
      setSelectedRows(stillSelected);
    }
    currentRow.checkbox = !currentRow.checkbox;
  };

  const mapNotificationListToTableRowView = (data: NotificationListPagedResponse["data"]): TableRowView[] => {
    if (!data) {
      return [];
    }
    return data.map((notificationList) => ({
      id: notificationList.id,
      checkbox: false,
      recipients: notificationList.recipients,
      applicationField: { ...notificationList.applicationField },
      marketSegment: { ...notificationList.applicationField.marketSegment },
      businessUnit: { ...notificationList.applicationField.marketSegment.businessUnit },
    }));
  };

  const businessUnitIds = useFindCommonIds(activeFilters.businessUnit, filterOptions?.businessUnitFilterOptions);
  const marketSegmentIds = useFindCommonIds(activeFilters.marketSegment, filterOptions?.marketSegmentFilterOptions);
  const applicationFieldIds = useFindCommonIds(
    activeFilters.applicationField,
    filterOptions?.applicationFieldFilterOptions,
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [rowsPerPage, activeFilters, activeUserFilters]);

  useEffect(() => {
    if (!isNotificationDialogOpen) {
      const recipientIds = activeUserFilters.recipients.map((recipient) => recipient.id);

      notificationListsApi
        .getNotificationLists(
          laboratoryId,
          recipientIds,
          applicationFieldIds,
          businessUnitIds,
          marketSegmentIds,
          currentPage,
          rowsPerPage,
        )
        .then(({ data }) => {
          const notificationListsView = mapNotificationListToTableRowView(data.data);
          setCurrentPage(data.pageIndex!); // page
          setRowsPerPage(data.pageSize!); // rowsPerPage
          setTotalPages(data.totalPages!);
          setTotalData(data.totalData!);
          setRows(notificationListsView);
          setFilterOptions(data.notificationListFilterOptionsResponse!);
          setSelectedRows([]);
        })
        .catch(handleHttpError)
        .finally(() => setIsLoading(false));
    }
  }, [currentPage, rowsPerPage, isNotificationDialogOpen, activeFilters, activeUserFilters]);

  if (isLoading) {
    return <CustomProgress />;
  }

  const allChecked = () => {
    const lastPage = totalPages - 1 === currentPage;
    if (lastPage) {
      const totalItemsOnFullPages = rowsPerPage * currentPage;
      const remainingItemsOnLastPage = totalData - totalItemsOnFullPages;
      return totalData > 0 && selectedRows.length === remainingItemsOnLastPage;
    }
    return totalData > 0 && selectedRows.length === rowsPerPage;
  };

  return (
    <>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {columns.map((column) => {
                if (column.id === "checkbox") {
                  return (
                    <TableCell key={column.id}>
                      <Checkbox checked={allChecked()} onChange={handleSelectAll} />
                    </TableCell>
                  );
                }
                if (column.id === "recipients") {
                  return (
                    <TableCell key={column.id}>
                      <div style={{ display: "flex" }}>
                        <Box component="span">{column.label}</Box>
                        <TableColumnUserFilter
                          laboratoryId={laboratoryId}
                          activeFilters={activeUserFilters.recipients}
                          popoverId={column.id}
                          onFilterChange={(data) => columnUserFilterChange(data)}
                        />
                      </div>
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={column.id}>
                    <div style={{ display: "flex" }}>
                      <Box component="span">{column.label}</Box>
                      <TestCasesOverviewTableFilterLabel
                        filterOptions={getFilterOptions(column.id)}
                        activeFilters={getActiveFilter(column.id)}
                        popoverId={column.id}
                        onFilterChange={(data) => columnFilterChange(data, column.id)}
                      />
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {rows.map((row: any) => (
              <TableRow hover key={uuid()} onClick={(event) => handleSelectRow(event, row)}>
                {columns.map((column) => {
                  if (column.id === "checkbox") {
                    return (
                      <TableCell key={column.id}>
                        <Checkbox checked={row.checkbox} />
                      </TableCell>
                    );
                  }
                  if (column.id === "recipients") {
                    return (
                      <TableCell key={column.id}>
                        <StyledTooltip
                          title={<>{row[column.id].map((recipient: Field) => recipient.name).join(", ")}</>}
                        >
                          <Box component="span">
                            {row[column.id].map((recipient: Field) => recipient.name).join(", ")}
                          </Box>
                        </StyledTooltip>
                      </TableCell>
                    );
                  }
                  return <TableCell key={column.id}>{row[column.id].name}</TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 200]}
        component="div"
        count={totalData}
        rowsPerPage={rowsPerPage}
        page={currentPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
      <Grid container item direction="row" justify="flex-start">
        <Box marginRight={2}>
          <CustomButton
            disabled={!selectedRows.length}
            text="Remove Users"
            onClick={() => {
              setAction(UserAction.REMOVE);
              setIsNotificationDialogOpen(true);
            }}
          />
        </Box>
        <Box>
          <CustomButton
            primary
            disabled={!selectedRows.length}
            text="Add Users"
            onClick={() => {
              setAction(UserAction.ADD);
              setIsNotificationDialogOpen(true);
            }}
          />
        </Box>
      </Grid>
    </>
  );
};

export default MultiSelectionTable;
